import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { Typography } from '@components/Typography';
import { MAX_STORE_CODE_LENGTH } from '@constants/companies';
import { useTranslation, useThrottle } from '@hooks';
import { ModalBase } from '@components/Modal';
import Button from '@components/Button';
import ImportValidationTable from '@components/ImportValidationTable';
import { accountActions, authActions } from '@actions';
import { companyService } from '@services';
import { handleApiError } from '@helpers/api';
import { descriptionContainsUrl } from '@helpers/locations';
import { hasInvalidNewPhone, isInvalidPhone } from '@helpers/phone';

const Modal = styled(ModalBase, { shouldForwardProp: prop => prop !== 'showValidation' })(({ showValidation }) => ({
    '& .modal-base-paper': {
        width: 'calc(100% - 32px)',
        maxWidth: showValidation ? 1100 : 564,
    },
    '& .modal-base-content': { padding: 16 },
}));

const FileInputContainer = styled('div')({
    display: 'flex',
    gap: 8,
    marginTop: 8,
    width: '100%',
    height: 32,
    alignItems: 'center',
});

const truncStyle = { maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const scrollStyle = {
    maxWidth: '200px',
    maxHeight: '60px',
    overflow: 'auto',
    display: 'block',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_REGEX = /^https?:\/\/.+/;

const LOCATION_TYPE_LABELS = {
    1: 'Location',
    2: 'Healthcare Facility',
    3: 'Healthcare Professional',
    4: 'Hotel',
    5: 'Restaurant',
};
const LOCATION_STATUS_LABELS = { 1: 'Open', 2: 'Closed' };
const PAYMENT_METHOD_LABELS = {
    1: 'Cash',
    2: 'Visa',
    3: 'Mastercard',
    4: 'Apple Pay',
    5: 'Android Pay',
    6: 'PayPal',
    7: 'Amex',
    8: 'Discover',
    9: 'Invoice',
    10: 'Bitcoin',
    11: 'Travelers Check',
    12: 'Diners Club',
    13: 'Insurance',
};
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatWorkingHours = wh => {
    if (!wh?.length) return null;
    return wh
        .map(d => {
            const day = DAY_NAMES[d.day] ?? d.day;
            const times = d.hours?.map(h => `${h.from}-${h.to}`).join(', ') || 'Closed';
            return `${day}: ${times}`;
        })
        .join('\n');
};

const formatSpecialHours = sh => {
    if (!sh?.length) return null;
    return sh
        .map(d =>
            !d.isOpen ? `${d.date}: Closed` : `${d.date}: ${(d.hours || []).map(h => `${h.from}-${h.to}`).join(', ')}`,
        )
        .join('\n');
};

// Mirror of server/Modules/Pluspoint.Modules.Core/Locations/Models/LocationField.cs.
// Numeric values must match the C# enum (append-only there).
const LocationField = Object.freeze({
    InternalName: 0,
    LocationName: 1,
    Type: 2,
    GeoModifier: 3,
    FirstName: 4,
    LastName: 5,
    Degree: 6,
    NationalHealthProviderId: 7,
    Office: 8,
    Headshot: 9,
    Status: 10,
    PhoneNumber: 11,
    PhoneExtension: 12,
    Email: 13,
    Website: 14,
    HasBusinessAddress: 19,
    AddressLine1: 20,
    AddressLine2: 21,
    City: 22,
    Region: 23,
    Country: 24,
    PostalCode: 25,
    Latitude: 26,
    Longitude: 27,
    ShortDescription: 28,
    MediumDescription: 29,
    LongDescription: 30,
    OpeningDate: 31,
    FeaturedMessage: 32,
    FeaturedMessageUrl: 33,
    WorkingHoursEnabled: 34,
    WorkingHours: 35,
    SpecialHours: 36,
    LogoUrl: 37,
    CoverUrl: 38,
    Categories: 39,
    Services: 40,
    PaymentMethods: 41,
    // TimeZone (42) is never imported via CSV.
    Certifications: 43,
    SocialLinks: 44,
    // Per-platform Social* values (45-51) are intentionally omitted: they're only sent by name (string enum)
    // from bulk edit and are not surfaced in this CSV-import diff table. Pinned in LocationFieldStabilityTests.
    // LocatedIn (52) is likewise omitted: it has no CSV column and is set only from the location form,
    // so it never appears in an import mask. Give it a definition below if that ever changes.
    AdditionalPhones: 54,
    StoreCode: 55,
});

const LOCATION_FIELD_DEFS = {
    [LocationField.InternalName]: {
        labelKey: 'SettingsCompanies.internalName',
        get: l => l.internalName,
        style: 'trunc',
    },
    [LocationField.LocationName]: {
        labelKey: 'SettingsCompanies.locationName',
        get: l => l.locationName,
        validate: l => !l.locationName?.trim(),
        style: 'trunc',
    },
    [LocationField.Type]: {
        labelKey: 'common.type',
        get: l => LOCATION_TYPE_LABELS[l.type] ?? (l.type != null ? String(l.type) : null),
        style: 'text',
    },
    [LocationField.GeoModifier]: { labelKey: 'SettingsCompanies.geoModifier', get: l => l.geoModifier, style: 'trunc' },
    [LocationField.FirstName]: { labelKey: 'common.firstName', get: l => l.firstName, style: 'trunc' },
    [LocationField.LastName]: { labelKey: 'common.lastName', get: l => l.lastName, style: 'trunc' },
    [LocationField.Degree]: { labelKey: 'SettingsCompanies.degree', get: l => l.degree, style: 'text' },
    [LocationField.NationalHealthProviderId]: {
        labelKey: 'SettingsCompanies.nationalHealthProviderId',
        get: l => l.nationalHealthProviderId,
        style: 'trunc',
    },
    [LocationField.StoreCode]: {
        labelKey: 'SettingsCompanies.storeCode',
        get: l => l.storeCode,
        validate: l => (l.storeCode?.length ?? 0) > MAX_STORE_CODE_LENGTH,
        style: 'trunc',
    },
    [LocationField.Office]: { labelKey: 'SettingsCompanies.office', get: l => l.office, style: 'trunc' },
    [LocationField.Headshot]: { labelKey: 'SettingsCompanies.headshotUrl', get: l => l.headshotUrl, style: 'trunc' },
    [LocationField.Status]: {
        labelKey: 'common.status',
        get: l => LOCATION_STATUS_LABELS[l.status] ?? (l.status != null ? String(l.status) : null),
        style: 'text',
    },
    [LocationField.PhoneNumber]: {
        labelKey: 'SettingsCompanies.primaryPhone',
        get: l => l.phoneNumber,
        validate: l => !!l.phoneNumber?.trim() && isInvalidPhone(l.phoneNumber),
        style: 'text',
    },
    [LocationField.PhoneExtension]: {
        labelKey: 'SettingsCompanies.phoneExtension',
        get: l => l.phoneExtension,
        style: 'text',
    },
    [LocationField.AdditionalPhones]: {
        labelKey: 'SettingsCompanies.additionalPhones',
        get: l => l.additionalPhones?.join(', ') || null,
        validate: (l, storedAdditionalPhones) =>
            hasInvalidNewPhone(l.additionalPhones, storedAdditionalPhones?.[l.companyId]),
        style: 'text',
    },
    [LocationField.Email]: {
        labelKey: 'common.email',
        get: l => l.email,
        validate: l => !!l.email && !EMAIL_REGEX.test(l.email),
        style: 'trunc',
    },
    [LocationField.Website]: {
        labelKey: 'SettingsCompanies.website',
        get: l => l.website,
        validate: l => !!l.website && !URL_REGEX.test(l.website),
        style: 'trunc',
    },
    [LocationField.HasBusinessAddress]: {
        labelKey: 'UpdateLocations.showAddress',
        get: l => l.hasBusinessAddress,
        style: 'bool',
    },
    [LocationField.AddressLine1]: {
        labelKey: 'SettingsCompanies.addressLine1',
        get: l => l.addressLine1,
        style: 'trunc',
    },
    [LocationField.AddressLine2]: {
        labelKey: 'SettingsCompanies.addressLine2',
        get: l => l.addressLine2,
        style: 'trunc',
    },
    [LocationField.City]: { labelKey: 'SettingsCompanies.city', get: l => l.city, style: 'trunc' },
    [LocationField.Region]: { labelKey: 'SettingsCompanies.state', get: l => l.region, style: 'trunc' },
    [LocationField.Country]: { labelKey: 'SettingsCompanies.country', get: l => l.country, style: 'text' },
    [LocationField.PostalCode]: { labelKey: 'SettingsCompanies.postalCode', get: l => l.postalCode, style: 'text' },
    [LocationField.Latitude]: {
        labelKey: 'common.latitude',
        get: l => l.latitude,
        validate: l => {
            if (!l.latitude) return false;
            const n = parseFloat(l.latitude);
            return isNaN(n) || n < -90 || n > 90;
        },
        style: 'text',
    },
    [LocationField.Longitude]: {
        labelKey: 'common.longitude',
        get: l => l.longitude,
        validate: l => {
            if (!l.longitude) return false;
            const n = parseFloat(l.longitude);
            return isNaN(n) || n < -180 || n > 180;
        },
        style: 'text',
    },
    [LocationField.ShortDescription]: {
        labelKey: 'SettingsCompanies.descriptionShort',
        get: l => l.shortDescription,
        validate: l =>
            !!l.shortDescription && (l.shortDescription.length > 75 || descriptionContainsUrl(l.shortDescription)),
        style: 'scroll',
    },
    [LocationField.MediumDescription]: {
        labelKey: 'SettingsCompanies.descriptionMedium',
        get: l => l.mediumDescription,
        validate: l =>
            !!l.mediumDescription && (l.mediumDescription.length > 500 || descriptionContainsUrl(l.mediumDescription)),
        style: 'scroll',
    },
    [LocationField.LongDescription]: {
        labelKey: 'SettingsCompanies.descriptionLong',
        get: l => l.longDescription,
        validate: l =>
            !!l.longDescription && (l.longDescription.length > 750 || descriptionContainsUrl(l.longDescription)),
        style: 'scroll',
    },
    [LocationField.OpeningDate]: { labelKey: 'SettingsCompanies.openingDate', get: l => l.openingDate, style: 'text' },
    [LocationField.FeaturedMessage]: {
        labelKey: 'UpdateLocations.featuredMessage',
        get: l => l.featuredMessage,
        style: 'trunc',
    },
    [LocationField.FeaturedMessageUrl]: {
        labelKey: 'UpdateLocations.featuredMessageUrl',
        get: l => l.featuredMessageUrl,
        validate: l => !!l.featuredMessageUrl && !URL_REGEX.test(l.featuredMessageUrl),
        style: 'trunc',
    },
    [LocationField.WorkingHoursEnabled]: {
        labelKey: 'UpdateLocations.showHours',
        get: l => l.workingHoursEnabled,
        style: 'bool',
    },
    [LocationField.WorkingHours]: {
        labelKey: 'SettingsCompanies.businessHours',
        get: l => formatWorkingHours(l.workingHours),
        style: 'scroll',
    },
    [LocationField.SpecialHours]: {
        labelKey: 'SettingsCompanies.specialHours',
        get: l => formatSpecialHours(l.specialHours),
        style: 'scroll',
    },
    [LocationField.LogoUrl]: { labelKey: 'common.logo', get: l => l.logoUrl, style: 'trunc' },
    [LocationField.CoverUrl]: { labelKey: 'SettingsCompanies.coverPhoto', get: l => l.coverUrl, style: 'trunc' },
    [LocationField.Categories]: {
        labelKey: 'Listings.categories',
        get: l =>
            l.categories
                ?.map(c => c.displayNameEn)
                .filter(Boolean)
                .join(', ') || null,
        style: 'trunc',
    },
    [LocationField.Services]: { labelKey: 'BulkEditLocations.services', get: l => l.services, style: 'trunc' },
    [LocationField.PaymentMethods]: {
        labelKey: 'common.paymentMethods',
        get: l => l.paymentMethods?.map(m => PAYMENT_METHOD_LABELS[m] ?? m).join(', ') || null,
        style: 'trunc',
    },
    [LocationField.Certifications]: {
        labelKey: 'SettingsCompanies.certifications',
        get: l => l.certifications?.filter(Boolean).join(', ') || null,
        style: 'trunc',
    },
};

const formatCellValue = val => {
    if (val === null || val === undefined || val === '') return null;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) {
        if (!val.length) return null;
        return val
            .map(v => (typeof v === 'string' ? v : v.name || v.categoryName || v.title || JSON.stringify(v)))
            .join(', ');
    }
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
};

const getVisibleDefs = mask => (mask?.fieldsToUpdate || []).map(n => LOCATION_FIELD_DEFS[n]).filter(Boolean);

const buildColumnNames = (mask, t) => [
    { key: 'idx', component: <Typography variant="subtitle2">#</Typography> },
    { key: 'status', component: <Typography variant="subtitle2">{t('common.status')}</Typography> },
    { key: 'id', component: <Typography variant="subtitle2">ID</Typography> },
    ...getVisibleDefs(mask).map((def, i) => ({
        key: i,
        component: <Typography variant="subtitle2">{t(def.labelKey)}</Typography>,
    })),
    { key: 'del', component: <Typography variant="subtitle2"></Typography> },
];

const buildLocationRows = (data, mask, storedAdditionalPhones, t, onDelete) => {
    const defs = getVisibleDefs(mask);
    return data.map((loc, index) => {
        const fieldErrors = defs.map(def => (def.validate ? def.validate(loc, storedAdditionalPhones) : false));
        const hasError = fieldErrors.some(Boolean);
        return [
            { component: <Typography variant="subtitle2">{index + 1}</Typography> },
            {
                component: (
                    <Chip
                        label={loc.companyId > 0 ? t('common.update') : t('common.new_one')}
                        size="small"
                        color={loc.companyId > 0 ? 'primary' : 'success'}
                    />
                ),
            },
            { component: <Typography variant="body2">{loc.companyId > 0 ? loc.companyId : '--'}</Typography> },
            ...defs.map((def, di) => {
                const raw = def.get(loc);
                const display = formatCellValue(raw);
                const isError = fieldErrors[di];
                if (def.style === 'bool') {
                    return {
                        component: (
                            <Typography variant="body2">
                                {raw === null || raw === undefined ? '--' : raw ? 'Yes' : 'No'}
                            </Typography>
                        ),
                    };
                }
                if (def.style === 'scroll') {
                    return {
                        component: (
                            <Typography
                                variant="body2"
                                sx={{ ...scrollStyle, ...(isError && { color: 'error.main' }) }}
                            >
                                {display || '--'}
                            </Typography>
                        ),
                    };
                }
                const style = def.style === 'trunc' ? truncStyle : undefined;
                return {
                    component: (
                        <Typography variant="body2" style={style} sx={isError ? { color: 'error.main' } : {}}>
                            {display || '--'}
                        </Typography>
                    ),
                };
            }),
            {
                component: (
                    <IconButton color={hasError ? 'error' : 'default'} onClick={() => onDelete(index)}>
                        <DeleteIcon />
                    </IconButton>
                ),
            },
        ];
    });
};

const UpdateLocations = ({ open, setOpen }) => {
    const [fileName, setFileName] = useState('');
    const [locationsData, setLocationsData] = useState({ data: [], updated: 0, updateMask: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [onDownloadClick] = useThrottle(async () => {
        try {
            const file = await companyService.getCompaniesCSV();
            const anchor = document.createElement('a');
            anchor.style.display = 'none';
            anchor.href = file;
            anchor.download = 'locations_import_example.csv';
            document.body.appendChild(anchor);
            anchor.click();
            window.URL.revokeObjectURL(file);
            anchor.remove();
        } catch (error) {
            handleApiError(error);
        }
    }, 1000);

    const submitCSV = async event => {
        try {
            const file = event.target.files[0];
            setFileName(file.name);
            const data = new FormData();
            data.append('file', event.target.files[0]);
            data.append('fileName', event.target.files[0].name);
            const result = await companyService.uploadCompaniesCSV(data);
            setLocationsData(result);
            setShowValidation(false);
        } catch (error) {
            handleApiError(error);
        }
        event.target.value = null;
    };

    const onDeleteLocation = index => {
        const copy = [...locationsData.data];
        copy.splice(index, 1);
        const updated = copy.filter(loc => loc.companyId > 0).length;
        setLocationsData(prev => ({ ...prev, data: copy, updated }));
    };

    const onUpdateLocations = async () => {
        setIsSubmitting(true);
        try {
            await companyService.updateCompaniesBulk(locationsData);
            await dispatch(accountActions.get());
            dispatch(authActions.getCurrentUser());
            setOpen(false);
        } catch (error) {
            handleApiError(error);
        }
        setIsSubmitting(false);
    };

    const defs = getVisibleDefs(locationsData.updateMask);
    const locationErrors = locationsData.data.map(loc =>
        defs.some(def => (def.validate ? def.validate(loc, locationsData.storedAdditionalPhones) : false)),
    );

    const toUpdate = locationsData.data.filter(loc => loc.companyId > 0).length;
    const toCreate = locationsData.data.length - toUpdate;

    const isOverQuota =
        locationsData.availableLocationsToAdd != null && toCreate > locationsData.availableLocationsToAdd;

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={() => setOpen(false)}
            showValidation={showValidation}
            title={t('UpdateLocations.title')}
            subtitle={!showValidation ? t('UpdateLocations.subtitle') : null}
            onPrimaryAction={!showValidation ? () => setShowValidation(true) : onUpdateLocations}
            primaryActionText={
                !showValidation
                    ? t('buttons.validate')
                    : t('UpdateLocations.importButton', { amount: locationsData.data.length })
            }
            primaryActionDisabled={
                !showValidation
                    ? !fileName || locationsData.data.length === 0
                    : isSubmitting || locationsData.data.length === 0 || locationErrors.some(Boolean) || isOverQuota
            }
            onSecondaryAction={showValidation ? () => setShowValidation(false) : null}
            secondaryActionText={t('buttons.back')}
        >
            {showValidation ? (
                <>
                    <Typography variant="caption" sx={{ mb: '16px', display: 'block', color: 'var(--text-secondary)' }}>
                        {t('UpdateLocations.dataVerificationDescription')}
                    </Typography>
                    {isOverQuota && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {t('UpdateLocations.quotaExceeded', {
                                count: toCreate,
                                available: locationsData.availableLocationsToAdd,
                            })}
                        </Alert>
                    )}
                    <ImportValidationTable
                        columnNames={buildColumnNames(locationsData.updateMask, t)}
                        rows={buildLocationRows(
                            locationsData.data,
                            locationsData.updateMask,
                            locationsData.storedAdditionalPhones,
                            t,
                            onDeleteLocation,
                        )}
                        hasError={locationErrors}
                    />
                </>
            ) : (
                <>
                    <Typography variant="subtitle2">1. {t('UpdateLocations.list1Title')}</Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}
                    >
                        {t('UpdateLocations.list1Description')}
                    </Typography>
                    <Button variant="outlined" onClick={onDownloadClick} sx={{ marginBottom: '24px' }}>
                        {t('buttons.download')}
                    </Button>
                    <Typography variant="subtitle2">2. {t('UpdateLocations.list2Title')}</Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}
                    >
                        {t('UpdateLocations.list2Description')}
                    </Typography>
                    <FileInputContainer>
                        <input
                            accept=".csv,.xlsx,.xls"
                            style={{ display: 'none' }}
                            id="raised-button-file"
                            type="file"
                            onChange={submitCSV}
                            name="contactsList"
                        />
                        <label htmlFor="raised-button-file">
                            <Button type="button" component="span" fullWidth variant="outlined">
                                {t(fileName ? 'buttons.replace' : 'buttons.upload')}
                            </Button>
                        </label>
                        <Typography variant="caption" color="textSecondary">
                            {fileName}
                        </Typography>
                    </FileInputContainer>
                    {locationsData.data?.length ? (
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--text-secondary)', marginTop: '8px', display: 'block' }}
                        >
                            {t('UpdateLocations.locationsToUpdate')}: {toUpdate}
                            {' · '}
                            {t('UpdateLocations.locationsToAdd')}: {toCreate}
                        </Typography>
                    ) : null}
                </>
            )}
        </Modal>
    );
};

export default UpdateLocations;
